import _class_call_check from "@swc/helpers/src/_class_call_check.mjs";
import _create_class from "@swc/helpers/src/_create_class.mjs";
import _define_property from "@swc/helpers/src/_define_property.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import invariant from "tiny-invariant";
import JSBI from "jsbi";
import { pack, keccak256 } from "@ethersproject/solidity";
import { getCreate2Address } from "@ethersproject/address";
import { Price } from "./fractions/price";
import { TokenAmount } from "./fractions/tokenAmount";
import { FACTORY_ADDRESS_MAP, INIT_CODE_HASH_MAP, MINIMUM_LIQUIDITY, ZERO, FIVE, FEES_NUMERATOR, FEES_DENOMINATOR } from "../constants";
import { sqrt, parseBigintIsh } from "../utils";
import { InsufficientReservesError, InsufficientInputAmountError } from "../errors";
import { Token } from "./token";
var PAIR_ADDRESS_CACHE = {};
var PAIR_ADDRESS_V3_CACHE = {};
var PAIR_CACHE = {};
var composeKey = function(token0, token1) {
    return "".concat(token0.chainId, "-").concat(token0.address, "-").concat(token1.address);
};
export var PairV3 = /*#__PURE__*/ function() {
    "use strict";
    function PairV3(tokenAmountA, tokenAmountB, poolType, fee) {
        _class_call_check(this, PairV3);
        this.exponent0 = "32";
        this.exponent1 = "32";
        this.poolType = 2;
        this.fee = 3;
        var tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
         ? [
            tokenAmountA,
            tokenAmountB
        ] : [
            tokenAmountB,
            tokenAmountA
        ];
        this.liquidityToken = new Token(tokenAmounts[0].token.chainId, Pair.getV3Address(tokenAmounts[0].token, tokenAmounts[1].token, poolType, fee), 18, "PE-LP", "EquitySwap LPs");
        this.tokenAmounts = tokenAmounts;
        this.poolType = poolType;
        this.fee = fee;
    }
    var _proto = PairV3.prototype;
    _proto.setExponents = function setExponents(_exponent0, _exponent1) {
        this.exponent0 = _exponent0;
        this.exponent1 = _exponent1;
    };
    /**
   * Returns true if the token is either token0 or token1
   * @param token to check
   */ _proto.involvesToken = function involvesToken(token) {
        return token.equals(this.token0) || token.equals(this.token1);
    };
    /**
   * Return the price of the given token in terms of the other token in the pair.
   * @param token token to return price of
   */ _proto.priceOf = function priceOf(token) {
        invariant(this.involvesToken(token), "TOKEN");
        return token.equals(this.token0) ? this.token0Price : this.token1Price;
    };
    _proto.reserveOf = function reserveOf(token) {
        invariant(this.involvesToken(token), "TOKEN");
        return token.equals(this.token0) ? this.reserve0 : this.reserve1;
    };
    _proto.exponentOf = function exponentOf(token) {
        invariant(this.involvesToken(token), "TOKEN");
        return token.equals(this.token0) ? this.exponent0 : this.exponent1;
    };
    // eslint-disable-next-line class-methods-use-this
    _proto.floorSqrt = function floorSqrt(n) {
        if (JSBI.GT(n, 0)) {
            var x = JSBI.add(JSBI.divide(n, JSBI.BigInt(2)), JSBI.BigInt(1));
            var y = JSBI.divide(JSBI.add(x, JSBI.divide(n, x)), JSBI.BigInt(2));
            while(JSBI.GT(x, y)){
                x = JSBI.add(y, JSBI.BigInt(0));
                y = JSBI.divide(JSBI.add(x, JSBI.divide(n, x)), JSBI.BigInt(2));
            }
            return x;
        }
        return JSBI.BigInt(0);
    };
    // eslint-disable-next-line class-methods-use-this
    _proto.floorCbrt = function floorCbrt(n) {
        var x = JSBI.BigInt(0);
        for(var y = JSBI.exponentiate(JSBI.BigInt(2), JSBI.BigInt(255)); JSBI.GT(y, JSBI.BigInt(0)); y = JSBI.divide(y, JSBI.BigInt(8))){
            x = JSBI.multiply(x, JSBI.BigInt(2));
            var z = JSBI.add(JSBI.multiply(JSBI.multiply(JSBI.BigInt(3), x), JSBI.add(x, JSBI.BigInt(1))), JSBI.BigInt(1));
            if (JSBI.GE(JSBI.divide(n, y), z)) {
                n = JSBI.subtract(n, JSBI.multiply(y, z));
                x = JSBI.add(x, JSBI.BigInt(1));
            }
        }
        return x;
    };
    _proto.exp = function exp(n, a, b, dec) {
        var decimal = 38;
        var decimal2 = 77;
        if (a === b) {
            return n;
        }
        var precisionThreshold = JSBI.BigInt(Math.pow(10, 8)); // 设定一个精度保护阈值
        if (JSBI.lessThan(n, precisionThreshold)) {
            // 当n非常小的时候，先放大一定的倍数
            n = JSBI.multiply(n, JSBI.BigInt(1000)); // 通过放大倍数来保留精度
        }
        if (a === 32 && b === 8) {
            n = JSBI.divide(JSBI.multiply(JSBI.multiply(n, n), JSBI.multiply(n, n)), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal2)));
        } else if (a === 32 && b === 1) {
            var pow64 = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal));
            var q = n;
            for(var i = 0; i < 31; i++){
                q = JSBI.divide(JSBI.multiply(q, n), pow64);
            }
            return q;
        } else if (a === 1 && b === 32) {
            var result = n;
            for(var i1 = 0; i1 < 5; i1++){
                var exponent = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal));
                result = JSBI.multiply(result, exponent);
                result = this.floorSqrt(result);
            }
            return result;
        } else if (a === 8 && b === 32) {
            n = this.floorSqrt(this.floorSqrt(JSBI.multiply(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal2)))));
        }
        return n;
    };
    _proto.cpoy2Exp = function cpoy2Exp(n, a, b) {
        var decimal = 38;
        var decimal2 = 77;
        if (a == b) {
            return n;
        }
        a = a / 32;
        b = b / 32;
        // 扩充到48位
        if (a == 1 && b == 4 || a == 2 && b == 4) {
            n = this.floorSqrt(JSBI.multiply(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal))));
        } else if (a == 2 && b == 1 || a == 4 && b == 2) {
            n = JSBI.divide(JSBI.multiply(n, n), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal)));
        } else if (a == 4 && b == 1) {
            n = JSBI.divide(JSBI.multiply(JSBI.multiply(n, n), JSBI.multiply(n, n)), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal2)));
        } else if (a == 1 && b == 4) {
            n = this.floorSqrt(this.floorSqrt(JSBI.multiply(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal2)))));
        }
        return n;
    };
    _proto.copyExp = function copyExp(n, a, b, decimal) {
        if (a == b) {
            return n;
        }
        a = a / 25;
        b = b / 25;
        // 扩充到48位
        n = JSBI.multiply(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(48 - decimal)));
        if (b == 2) {
            n = this.floorSqrt(n);
        } else if (b == 3) {
            n = this.floorCbrt(n);
        } else if (b == 4) {
            n = this.floorSqrt(this.floorSqrt(n));
        }
        n = JSBI.exponentiate(n, JSBI.BigInt(a));
        return JSBI.divide(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(48 * a / b - decimal)));
    };
    _proto.getOutputAmount = function getOutputAmount(inputAmount) {
        invariant(this.involvesToken(inputAmount.token), "TOKEN");
        if (JSBI.equal(this.reserve0.raw, ZERO) || JSBI.equal(this.reserve1.raw, ZERO)) {
            throw new InsufficientReservesError();
        }
        var inputReserve = this.reserveOf(inputAmount.token);
        var outputReserve = this.reserveOf(inputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var inputExponent = this.exponentOf(inputAmount.token);
        var outputExponent = this.exponentOf(inputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var feeBigInt = JSBI.BigInt(this.fee);
        var tenTimesFee = JSBI.multiply(feeBigInt, JSBI.BigInt(10));
        var FEES_NUM = JSBI.subtract(JSBI.BigInt(10000), tenTimesFee);
        // Fees I*F_N/F_D  
        var inputAmountWithFee = JSBI.divide(JSBI.multiply(inputAmount.raw, FEES_NUM), FEES_DENOMINATOR);
        var outputDecimals = inputAmount.token.equals(this.token0) ? this.token1.decimals : this.token0.decimals;
        var K = JSBI.multiply(this.exp(inputReserve.raw, +inputExponent, 32, inputAmount.currency.decimals), this.exp(outputReserve.raw, +outputExponent, 32, outputDecimals));
        var X = this.exp(JSBI.add(inputReserve.raw, inputAmountWithFee), +inputExponent, 32, inputAmount.currency.decimals);
        var tmp = this.exp(JSBI.divide(K, X), 32, +outputExponent, outputDecimals);
        if (JSBI.LE(JSBI.BigInt(outputReserve.raw), tmp)) {
            throw new InsufficientInputAmountError();
        }
        var outputAmount = new TokenAmount(inputAmount.token.equals(this.token0) ? this.token1 : this.token0, JSBI.subtract(JSBI.BigInt(outputReserve.raw), tmp));
        return [
            outputAmount,
            new PairV3(inputReserve.add(inputAmount), outputReserve.subtract(outputAmount), this.poolType, this.fee)
        ];
    };
    _proto.getInputAmount = function getInputAmount(outputAmount) {
        invariant(this.involvesToken(outputAmount.token), "TOKEN");
        if (JSBI.equal(this.reserve0.raw, ZERO) || JSBI.equal(this.reserve1.raw, ZERO) || JSBI.greaterThanOrEqual(outputAmount.raw, this.reserveOf(outputAmount.token).raw)) {
            throw new InsufficientReservesError();
        }
        var outputReserve = this.reserveOf(outputAmount.token);
        var inputReserve = this.reserveOf(outputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var outputExponent = this.exponentOf(outputAmount.token);
        var inputExponent = this.exponentOf(outputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var inputDecimals = outputAmount.token.equals(this.token0) ? this.token1.decimals : this.token0.decimals;
        var K = JSBI.multiply(this.exp(inputReserve.raw, +inputExponent, 32, inputDecimals), this.exp(outputReserve.raw, +outputExponent, 32, outputAmount.token.decimals));
        var Y = this.exp(JSBI.subtract(outputReserve.raw, outputAmount.raw), +outputExponent, 32, outputAmount.currency.decimals);
        var tmp = this.exp(JSBI.divide(K, Y), 32, +inputExponent, inputDecimals);
        if (JSBI.LT(tmp, JSBI.BigInt(inputReserve.raw))) {
            throw new InsufficientInputAmountError();
        }
        var feeBigInt = JSBI.BigInt(this.fee);
        var tenTimesFee = JSBI.multiply(feeBigInt, JSBI.BigInt(10));
        var FEES_NUM = JSBI.subtract(JSBI.BigInt(10000), tenTimesFee);
        var inputAmount = new TokenAmount(outputAmount.token.equals(this.token0) ? this.token1 : this.token0, JSBI.divide(JSBI.multiply(JSBI.subtract(tmp, JSBI.BigInt(inputReserve.raw)), JSBI.BigInt(FEES_DENOMINATOR)), FEES_NUM));
        return [
            inputAmount,
            new PairV3(inputReserve.add(inputAmount), outputReserve.subtract(outputAmount), this.poolType, this.fee)
        ];
    };
    _proto.getLiquidityMinted = function getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB) {
        invariant(totalSupply.token.equals(this.liquidityToken), "LIQUIDITY");
        var tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
         ? [
            tokenAmountA,
            tokenAmountB
        ] : [
            tokenAmountB,
            tokenAmountA
        ];
        invariant(tokenAmounts[0].token.equals(this.token0) && tokenAmounts[1].token.equals(this.token1), "TOKEN");
        var liquidity;
        if (JSBI.equal(totalSupply.raw, ZERO)) {
            liquidity = JSBI.subtract(sqrt(JSBI.multiply(tokenAmounts[0].raw, tokenAmounts[1].raw)), MINIMUM_LIQUIDITY);
        } else {
            var amount0 = JSBI.divide(JSBI.multiply(tokenAmounts[0].raw, totalSupply.raw), this.reserve0.raw);
            var amount1 = JSBI.divide(JSBI.multiply(tokenAmounts[1].raw, totalSupply.raw), this.reserve1.raw);
            liquidity = JSBI.lessThanOrEqual(amount0, amount1) ? amount0 : amount1;
        }
        if (!JSBI.greaterThan(liquidity, ZERO)) {
            throw new InsufficientInputAmountError();
        }
        return new TokenAmount(this.liquidityToken, liquidity);
    };
    _proto.getLiquidityValue = function getLiquidityValue(token, totalSupply, liquidity) {
        var feeOn = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : false, kLast = arguments.length > 4 ? arguments[4] : void 0;
        invariant(this.involvesToken(token), "TOKEN");
        invariant(totalSupply.token.equals(this.liquidityToken), "TOTAL_SUPPLY");
        invariant(liquidity.token.equals(this.liquidityToken), "LIQUIDITY");
        invariant(JSBI.lessThanOrEqual(liquidity.raw, totalSupply.raw), "LIQUIDITY");
        var totalSupplyAdjusted;
        if (!feeOn) {
            totalSupplyAdjusted = totalSupply;
        } else {
            invariant(!!kLast, "K_LAST");
            var kLastParsed = parseBigintIsh(kLast);
            if (!JSBI.equal(kLastParsed, ZERO)) {
                var rootK = sqrt(JSBI.multiply(this.reserve0.raw, this.reserve1.raw));
                var rootKLast = sqrt(kLastParsed);
                if (JSBI.greaterThan(rootK, rootKLast)) {
                    var numerator = JSBI.multiply(totalSupply.raw, JSBI.subtract(rootK, rootKLast));
                    var denominator = JSBI.add(JSBI.multiply(rootK, FIVE), rootKLast);
                    var feeLiquidity = JSBI.divide(numerator, denominator);
                    totalSupplyAdjusted = totalSupply.add(new TokenAmount(this.liquidityToken, feeLiquidity));
                } else {
                    totalSupplyAdjusted = totalSupply;
                }
            } else {
                totalSupplyAdjusted = totalSupply;
            }
        }
        return new TokenAmount(token, JSBI.divide(JSBI.multiply(liquidity.raw, this.reserveOf(token).raw), totalSupplyAdjusted.raw));
    };
    PairV3.getAddress = function getAddress(tokenA, tokenB) {
        var ref // does safety checks
         = _sliced_to_array(tokenA.sortsBefore(tokenB) ? [
            tokenA,
            tokenB
        ] : [
            tokenB,
            tokenA
        ], 2), token0 = ref[0], token1 = ref[1];
        var key = composeKey(token0, token1);
        if ((PAIR_ADDRESS_CACHE === null || PAIR_ADDRESS_CACHE === void 0 ? void 0 : PAIR_ADDRESS_CACHE[key]) === undefined) {
            PAIR_ADDRESS_CACHE = _object_spread_props(_object_spread({}, PAIR_ADDRESS_CACHE), _define_property({}, key, getCreate2Address(FACTORY_ADDRESS_MAP[token0.chainId], keccak256([
                "bytes"
            ], [
                pack([
                    "address",
                    "address"
                ], [
                    token0.address,
                    token1.address
                ])
            ]), INIT_CODE_HASH_MAP[token0.chainId])));
        }
        return PAIR_ADDRESS_CACHE[key];
    };
    PairV3.getV3Address = function getV3Address(tokenA, tokenB, poolType, fee) {
        // 确保排序的正确性，保障 token0 总是 sortsBefore token1
        var ref = _sliced_to_array(tokenA.sortsBefore(tokenB) ? [
            tokenA,
            tokenB
        ] : [
            tokenB,
            tokenA
        ], 2), token0 = ref[0], token1 = ref[1];
        // 修改 key 使其包含 poolType 和 fee
        var key = "".concat(composeKey(token0, token1), "-").concat(poolType, "-").concat(fee);
        // 如果缓存中不存在当前组合的地址，则进行计算并缓存
        if (!(PAIR_ADDRESS_V3_CACHE === null || PAIR_ADDRESS_V3_CACHE === void 0 ? void 0 : PAIR_ADDRESS_V3_CACHE[key])) {
            PAIR_ADDRESS_V3_CACHE = _object_spread_props(_object_spread({}, PAIR_ADDRESS_V3_CACHE), _define_property({}, key, getCreate2Address(// @ts-ignore
            FACTORY_ADDRESS_MAP[token0.chainId], keccak256([
                "bytes"
            ], [
                pack([
                    "address",
                    "address",
                    "uint8",
                    "uint"
                ], [
                    token0.address,
                    token1.address,
                    poolType,
                    fee
                ])
            ]), // @ts-ignore
            INIT_CODE_HASH_MAP[token0.chainId])));
        }
        // 返回缓存的或新计算的地址
        return PAIR_ADDRESS_V3_CACHE[key];
    };
    PairV3.getTestAddress = function getTestAddress() {
        var a = getCreate2Address("0x97a65044C818472dd3ae475Dd93cADBaEE2eEcfc", keccak256([
            "bytes"
        ], [
            pack([
                "address",
                "address",
                "uint8",
                "uint"
            ], [
                "0x11D62Bffe1dF959C89B2a52743347FA95BF84E8F",
                "0x1E1057724472eb13A165dAd6EF571Ae87B792865",
                "2",
                "3"
            ])
        ]), "0x658d728f516cb1eab6bab3d31d53f3e93875199b7205dfadf722888528c6abad");
        return a;
    };
    _create_class(PairV3, [
        {
            key: "token0Price",
            get: /**
   * Returns the current mid price of the pair in terms of token0, i.e. the ratio of reserve1 to reserve0
   */ function get() {
                return new Price(this.token0, this.token1, this.tokenAmounts[0].raw, this.tokenAmounts[1].raw);
            }
        },
        {
            key: "token1Price",
            get: /**
   * Returns the current mid price of the pair in terms of token1, i.e. the ratio of reserve0 to reserve1
   */ function get() {
                return new Price(this.token1, this.token0, this.tokenAmounts[1].raw, this.tokenAmounts[0].raw);
            }
        },
        {
            key: "chainId",
            get: /**
   * Returns the chain ID of the tokens in the pair.
   */ function get() {
                return this.token0.chainId;
            }
        },
        {
            key: "token0",
            get: function get() {
                return this.tokenAmounts[0].token;
            }
        },
        {
            key: "token1",
            get: function get() {
                return this.tokenAmounts[1].token;
            }
        },
        {
            key: "reserve0",
            get: function get() {
                return this.tokenAmounts[0];
            }
        },
        {
            key: "reserve1",
            get: function get() {
                return this.tokenAmounts[1];
            }
        }
    ]);
    return PairV3;
}();
export var Pair = /*#__PURE__*/ function() {
    "use strict";
    function Pair(tokenAmountA, tokenAmountB) {
        _class_call_check(this, Pair);
        this.exponent0 = "32";
        this.exponent1 = "32";
        var tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
         ? [
            tokenAmountA,
            tokenAmountB
        ] : [
            tokenAmountB,
            tokenAmountA
        ];
        this.liquidityToken = new Token(tokenAmounts[0].token.chainId, Pair.getAddress(tokenAmounts[0].token, tokenAmounts[1].token), 18, "PE-LP", "EquitySwap LPs");
        this.tokenAmounts = tokenAmounts;
    }
    var _proto = Pair.prototype;
    _proto.setExponents = function setExponents(_exponent0, _exponent1) {
        this.exponent0 = _exponent0;
        this.exponent1 = _exponent1;
    };
    /**
   * Returns true if the token is either token0 or token1
   * @param token to check
   */ _proto.involvesToken = function involvesToken(token) {
        return token.equals(this.token0) || token.equals(this.token1);
    };
    /**
   * Return the price of the given token in terms of the other token in the pair.
   * @param token token to return price of
   */ _proto.priceOf = function priceOf(token) {
        invariant(this.involvesToken(token), "TOKEN");
        return token.equals(this.token0) ? this.token0Price : this.token1Price;
    };
    _proto.reserveOf = function reserveOf(token) {
        invariant(this.involvesToken(token), "TOKEN");
        return token.equals(this.token0) ? this.reserve0 : this.reserve1;
    };
    _proto.exponentOf = function exponentOf(token) {
        invariant(this.involvesToken(token), "TOKEN");
        return token.equals(this.token0) ? this.exponent0 : this.exponent1;
    };
    // eslint-disable-next-line class-methods-use-this
    _proto.floorSqrt = function floorSqrt(n) {
        if (JSBI.GT(n, 0)) {
            var x = JSBI.add(JSBI.divide(n, JSBI.BigInt(2)), JSBI.BigInt(1));
            var y = JSBI.divide(JSBI.add(x, JSBI.divide(n, x)), JSBI.BigInt(2));
            while(JSBI.GT(x, y)){
                x = JSBI.add(y, JSBI.BigInt(0));
                y = JSBI.divide(JSBI.add(x, JSBI.divide(n, x)), JSBI.BigInt(2));
            }
            return x;
        }
        return JSBI.BigInt(0);
    };
    // eslint-disable-next-line class-methods-use-this
    _proto.floorCbrt = function floorCbrt(n) {
        var x = JSBI.BigInt(0);
        for(var y = JSBI.exponentiate(JSBI.BigInt(2), JSBI.BigInt(255)); JSBI.GT(y, JSBI.BigInt(0)); y = JSBI.divide(y, JSBI.BigInt(8))){
            x = JSBI.multiply(x, JSBI.BigInt(2));
            var z = JSBI.add(JSBI.multiply(JSBI.multiply(JSBI.BigInt(3), x), JSBI.add(x, JSBI.BigInt(1))), JSBI.BigInt(1));
            if (JSBI.GE(JSBI.divide(n, y), z)) {
                n = JSBI.subtract(n, JSBI.multiply(y, z));
                x = JSBI.add(x, JSBI.BigInt(1));
            }
        }
        return x;
    };
    _proto.calculateProduct = function calculateProduct(n, a, b, decimal) {};
    _proto.exp = function exp(n, a, b) {
        var decimal = 38;
        var decimal2 = 77;
        if (a === b) {
            // console.info("1:1")
            return n;
        }
        // console.info(a, b)
        // 扩充到48位
        if (a === 32 && b === 8) {
            // console.info("4:1")
            n = JSBI.divide(JSBI.multiply(JSBI.multiply(n, n), JSBI.multiply(n, n)), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal2)));
        } else if (a === 32 && b === 1) {
            ;
            var pow64 = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal));
            var q = n;
            for(var i = 0; i < 31; i++){
                q = JSBI.divide(JSBI.multiply(q, n), pow64);
            }
            // n = JSBI.divide(JSBI.multiply(q, n), pow64)
            return q;
        } else if (a === 1 && b === 32) {
            // console.info("1:32")
            var result = n;
            for(var i1 = 0; i1 < 5; i1++){
                var exponent = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal));
                result = JSBI.multiply(result, exponent);
                result = this.floorSqrt(result);
            }
            return result;
        } else if (a === 8 && b === 32) {
            // console.info("1:4")
            n = this.floorSqrt(this.floorSqrt(JSBI.multiply(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal2)))));
        }
        return n;
    };
    _proto.cpoy2Exp = function cpoy2Exp(n, a, b) {
        var decimal = 38;
        var decimal2 = 77;
        if (a == b) {
            return n;
        }
        a = a / 32;
        b = b / 32;
        // 扩充到48位
        if (a == 1 && b == 4 || a == 2 && b == 4) {
            n = this.floorSqrt(JSBI.multiply(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal))));
        } else if (a == 2 && b == 1 || a == 4 && b == 2) {
            n = JSBI.divide(JSBI.multiply(n, n), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal)));
        } else if (a == 4 && b == 1) {
            n = JSBI.divide(JSBI.multiply(JSBI.multiply(n, n), JSBI.multiply(n, n)), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal2)));
        } else if (a == 1 && b == 4) {
            n = this.floorSqrt(this.floorSqrt(JSBI.multiply(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal2)))));
        }
        return n;
    };
    _proto.copyExp = function copyExp(n, a, b, decimal) {
        if (a == b) {
            return n;
        }
        a = a / 25;
        b = b / 25;
        // 扩充到48位
        n = JSBI.multiply(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(48 - decimal)));
        if (b == 2) {
            n = this.floorSqrt(n);
        } else if (b == 3) {
            n = this.floorCbrt(n);
        } else if (b == 4) {
            n = this.floorSqrt(this.floorSqrt(n));
        }
        n = JSBI.exponentiate(n, JSBI.BigInt(a));
        return JSBI.divide(n, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(48 * a / b - decimal)));
    };
    _proto.getOutputAmount = function getOutputAmount(inputAmount) {
        invariant(this.involvesToken(inputAmount.token), "TOKEN");
        if (JSBI.equal(this.reserve0.raw, ZERO) || JSBI.equal(this.reserve1.raw, ZERO)) {
            throw new InsufficientReservesError();
        }
        var inputReserve = this.reserveOf(inputAmount.token);
        var outputReserve = this.reserveOf(inputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var inputExponent = this.exponentOf(inputAmount.token);
        var outputExponent = this.exponentOf(inputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        // Fees I*F_N/F_D  
        var inputAmountWithFee = JSBI.divide(JSBI.multiply(inputAmount.raw, FEES_NUMERATOR), FEES_DENOMINATOR);
        var outputDecimals = inputAmount.token.equals(this.token0) ? this.token1.decimals : this.token0.decimals;
        var K = JSBI.multiply(this.exp(inputReserve.raw, +inputExponent, 32, inputAmount.currency.decimals), this.exp(outputReserve.raw, +outputExponent, 32, outputDecimals));
        var X = this.exp(JSBI.add(inputReserve.raw, inputAmountWithFee), +inputExponent, 32, inputAmount.currency.decimals);
        var tmp = this.exp(JSBI.divide(K, X), 32, +outputExponent, outputDecimals);
        if (JSBI.LE(JSBI.BigInt(outputReserve.raw), tmp)) {
            throw new InsufficientInputAmountError();
        }
        var outputAmount = new TokenAmount(inputAmount.token.equals(this.token0) ? this.token1 : this.token0, JSBI.subtract(JSBI.BigInt(outputReserve.raw), tmp));
        // console.info('result:', JSBI.toNumber(JSBI.subtract(JSBI.BigInt(outputReserve.raw), tmp)))
        return [
            outputAmount,
            new Pair(inputReserve.add(inputAmount), outputReserve.subtract(outputAmount))
        ];
    };
    _proto.getInputAmount = function getInputAmount(outputAmount) {
        invariant(this.involvesToken(outputAmount.token), "TOKEN");
        if (JSBI.equal(this.reserve0.raw, ZERO) || JSBI.equal(this.reserve1.raw, ZERO) || JSBI.greaterThanOrEqual(outputAmount.raw, this.reserveOf(outputAmount.token).raw)) {
            throw new InsufficientReservesError();
        }
        var outputReserve = this.reserveOf(outputAmount.token);
        var inputReserve = this.reserveOf(outputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var outputExponent = this.exponentOf(outputAmount.token);
        var inputExponent = this.exponentOf(outputAmount.token.equals(this.token0) ? this.token1 : this.token0);
        var inputDecimals = outputAmount.token.equals(this.token0) ? this.token1.decimals : this.token0.decimals;
        var K = JSBI.multiply(this.exp(inputReserve.raw, +inputExponent, 32, inputDecimals), this.exp(outputReserve.raw, +outputExponent, 32, outputAmount.token.decimals));
        var Y = this.exp(JSBI.subtract(outputReserve.raw, outputAmount.raw), +outputExponent, 32, outputAmount.currency.decimals);
        var tmp = this.exp(JSBI.divide(K, Y), 32, +inputExponent, inputDecimals);
        if (JSBI.LT(tmp, JSBI.BigInt(inputReserve.raw))) {
            throw new InsufficientInputAmountError();
        }
        var inputAmount = new TokenAmount(outputAmount.token.equals(this.token0) ? this.token1 : this.token0, JSBI.divide(JSBI.multiply(JSBI.subtract(tmp, JSBI.BigInt(inputReserve.raw)), JSBI.BigInt(FEES_DENOMINATOR)), FEES_NUMERATOR));
        return [
            inputAmount,
            new Pair(inputReserve.add(inputAmount), outputReserve.subtract(outputAmount))
        ];
    };
    _proto.getLiquidityMinted = function getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB) {
        invariant(totalSupply.token.equals(this.liquidityToken), "LIQUIDITY");
        var tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
         ? [
            tokenAmountA,
            tokenAmountB
        ] : [
            tokenAmountB,
            tokenAmountA
        ];
        invariant(tokenAmounts[0].token.equals(this.token0) && tokenAmounts[1].token.equals(this.token1), "TOKEN");
        var liquidity;
        if (JSBI.equal(totalSupply.raw, ZERO)) {
            liquidity = JSBI.subtract(sqrt(JSBI.multiply(tokenAmounts[0].raw, tokenAmounts[1].raw)), MINIMUM_LIQUIDITY);
        } else {
            var amount0 = JSBI.divide(JSBI.multiply(tokenAmounts[0].raw, totalSupply.raw), this.reserve0.raw);
            var amount1 = JSBI.divide(JSBI.multiply(tokenAmounts[1].raw, totalSupply.raw), this.reserve1.raw);
            liquidity = JSBI.lessThanOrEqual(amount0, amount1) ? amount0 : amount1;
        }
        if (!JSBI.greaterThan(liquidity, ZERO)) {
            throw new InsufficientInputAmountError();
        }
        return new TokenAmount(this.liquidityToken, liquidity);
    };
    _proto.getLiquidityValue = function getLiquidityValue(token, totalSupply, liquidity) {
        var feeOn = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : false, kLast = arguments.length > 4 ? arguments[4] : void 0;
        invariant(this.involvesToken(token), "TOKEN");
        invariant(totalSupply.token.equals(this.liquidityToken), "TOTAL_SUPPLY");
        invariant(liquidity.token.equals(this.liquidityToken), "LIQUIDITY");
        invariant(JSBI.lessThanOrEqual(liquidity.raw, totalSupply.raw), "LIQUIDITY");
        var totalSupplyAdjusted;
        if (!feeOn) {
            totalSupplyAdjusted = totalSupply;
        } else {
            invariant(!!kLast, "K_LAST");
            var kLastParsed = parseBigintIsh(kLast);
            if (!JSBI.equal(kLastParsed, ZERO)) {
                var rootK = sqrt(JSBI.multiply(this.reserve0.raw, this.reserve1.raw));
                var rootKLast = sqrt(kLastParsed);
                if (JSBI.greaterThan(rootK, rootKLast)) {
                    var numerator = JSBI.multiply(totalSupply.raw, JSBI.subtract(rootK, rootKLast));
                    var denominator = JSBI.add(JSBI.multiply(rootK, FIVE), rootKLast);
                    var feeLiquidity = JSBI.divide(numerator, denominator);
                    totalSupplyAdjusted = totalSupply.add(new TokenAmount(this.liquidityToken, feeLiquidity));
                } else {
                    totalSupplyAdjusted = totalSupply;
                }
            } else {
                totalSupplyAdjusted = totalSupply;
            }
        }
        return new TokenAmount(token, JSBI.divide(JSBI.multiply(liquidity.raw, this.reserveOf(token).raw), totalSupplyAdjusted.raw));
    };
    Pair.getAddress = function getAddress(tokenA, tokenB) {
        var ref // does safety checks
         = _sliced_to_array(tokenA.sortsBefore(tokenB) ? [
            tokenA,
            tokenB
        ] : [
            tokenB,
            tokenA
        ], 2), token0 = ref[0], token1 = ref[1];
        var key = composeKey(token0, token1);
        if ((PAIR_ADDRESS_CACHE === null || PAIR_ADDRESS_CACHE === void 0 ? void 0 : PAIR_ADDRESS_CACHE[key]) === undefined) {
            PAIR_ADDRESS_CACHE = _object_spread_props(_object_spread({}, PAIR_ADDRESS_CACHE), _define_property({}, key, getCreate2Address(FACTORY_ADDRESS_MAP[token0.chainId], keccak256([
                "bytes"
            ], [
                pack([
                    "address",
                    "address"
                ], [
                    token0.address,
                    token1.address
                ])
            ]), INIT_CODE_HASH_MAP[token0.chainId])));
        }
        return PAIR_ADDRESS_CACHE[key];
    };
    Pair.getV3Address = function getV3Address(tokenA, tokenB, poolType, fee) {
        // 确保排序的正确性，保障 token0 总是 sortsBefore token1
        var ref = _sliced_to_array(tokenA.sortsBefore(tokenB) ? [
            tokenA,
            tokenB
        ] : [
            tokenB,
            tokenA
        ], 2), token0 = ref[0], token1 = ref[1];
        // 修改 key 使其包含 poolType 和 fee
        var key = "".concat(composeKey(token0, token1), "-").concat(poolType, "-").concat(fee);
        // 如果缓存中不存在当前组合的地址，则进行计算并缓存
        if (!(PAIR_ADDRESS_V3_CACHE === null || PAIR_ADDRESS_V3_CACHE === void 0 ? void 0 : PAIR_ADDRESS_V3_CACHE[key])) {
            PAIR_ADDRESS_V3_CACHE = _object_spread_props(_object_spread({}, PAIR_ADDRESS_V3_CACHE), _define_property({}, key, getCreate2Address(FACTORY_ADDRESS_MAP[token0.chainId], keccak256([
                "bytes"
            ], [
                pack([
                    "address",
                    "address",
                    "uint8",
                    "uint"
                ], [
                    token0.address,
                    token1.address,
                    poolType,
                    fee
                ])
            ]), INIT_CODE_HASH_MAP[token0.chainId])));
        }
        // 返回缓存的或新计算的地址
        return PAIR_ADDRESS_V3_CACHE[key];
    };
    Pair.getTestAddress = function getTestAddress() {
        var a = getCreate2Address("0x97a65044C818472dd3ae475Dd93cADBaEE2eEcfc", keccak256([
            "bytes"
        ], [
            pack([
                "address",
                "address",
                "uint8",
                "uint"
            ], [
                "0x11D62Bffe1dF959C89B2a52743347FA95BF84E8F",
                "0x1E1057724472eb13A165dAd6EF571Ae87B792865",
                "2",
                "3"
            ])
        ]), "0x658d728f516cb1eab6bab3d31d53f3e93875199b7205dfadf722888528c6abad");
        return a;
    };
    _create_class(Pair, [
        {
            key: "token0Price",
            get: /**
   * Returns the current mid price of the pair in terms of token0, i.e. the ratio of reserve1 to reserve0
   */ function get() {
                return new Price(this.token0, this.token1, this.tokenAmounts[0].raw, this.tokenAmounts[1].raw);
            }
        },
        {
            key: "token1Price",
            get: /**
   * Returns the current mid price of the pair in terms of token1, i.e. the ratio of reserve0 to reserve1
   */ function get() {
                return new Price(this.token1, this.token0, this.tokenAmounts[1].raw, this.tokenAmounts[0].raw);
            }
        },
        {
            key: "chainId",
            get: /**
   * Returns the chain ID of the tokens in the pair.
   */ function get() {
                return this.token0.chainId;
            }
        },
        {
            key: "token0",
            get: function get() {
                return this.tokenAmounts[0].token;
            }
        },
        {
            key: "token1",
            get: function get() {
                return this.tokenAmounts[1].token;
            }
        },
        {
            key: "reserve0",
            get: function get() {
                return this.tokenAmounts[0];
            }
        },
        {
            key: "reserve1",
            get: function get() {
                return this.tokenAmounts[1];
            }
        }
    ]);
    return Pair;
}();
